import { useCallback, useMemo, useState } from 'react';
import {
  Add,
  Edit,
  CheckCircle,
  Cancel,
  Schedule,
  Block,
  Inventory2,
  SwapHoriz,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';

import { LoadingState } from './loading-state';
import { formatDateTime } from '../utils/formatting';
import {
  useGetBundleAssignmentsQuery,
  useCreateBundleAssignmentMutation,
  useUpdateBundleAssignmentStatusMutation,
  useReplaceBundleAssignmentMutation,
  useGetBundlesQuery,
} from '../store/hooks';
import type { BundleAssignmentResponse, BundleResponse } from '@store/generated/admin';

// Extended type to include bundle_type which is returned by API but not in generated types
type BundleAssignmentWithType = BundleAssignmentResponse & {
  bundle_type?: string | null;
};

interface ProviderBundleAssignmentsSectionProps {
  userId: string;
  organizationId: string;
}

const STATUS_OPTIONS = ['all', 'active', 'pending', 'canceled', 'expired'] as const;

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'active':
      return <CheckCircle fontSize="small" className="text-green-600" />;
    case 'pending':
      return <Schedule fontSize="small" className="text-yellow-600" />;
    case 'canceled':
      return <Cancel fontSize="small" className="text-red-600" />;
    case 'expired':
      return <Block fontSize="small" className="text-gray-500" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
};

export function ProviderBundleAssignmentsSection({
  userId,
  organizationId,
}: ProviderBundleAssignmentsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<BundleAssignmentWithType | null>(null);
  const [newAssignment, setNewAssignment] = useState({
    bundle: null as BundleResponse | null,
    effectiveFrom: null as Date | null,
    effectiveTo: null as Date | null,
  });
  const [editStatus, setEditStatus] = useState('');
  const [editEffectiveTo, setEditEffectiveTo] = useState<Date | null>(null);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceBundle, setReplaceBundle] = useState<BundleResponse | null>(null);

  const {
    data: assignmentsData,
    isLoading,
    refetch,
  } = useGetBundleAssignmentsQuery({
    organizationId,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const { data: bundlesData } = useGetBundlesQuery();

  const [createAssignment, { isLoading: isCreating }] = useCreateBundleAssignmentMutation();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateBundleAssignmentStatusMutation();
  const [replaceAssignment, { isLoading: isReplacing }] = useReplaceBundleAssignmentMutation();

  const allAssignments = assignmentsData?.assignments || [];
  const bundles = bundlesData?.bundles || [];

  // Filter to only show assignments for this specific user
  // (API filters by status, we filter by user_id client-side)
  const userAssignments = useMemo(() => {
    return allAssignments.filter(a => a.user_id === userId);
  }, [allAssignments, userId]);

  const handleCreateOpen = useCallback(() => {
    setNewAssignment({
      bundle: null,
      effectiveFrom: null,
      effectiveTo: null,
    });
    setCreateDialogOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateDialogOpen(false);
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    if (!newAssignment.bundle) return;

    try {
      await createAssignment({
        createAssignmentRequest: {
          bundle_id: newAssignment.bundle.bundle_id,
          organization_id: organizationId,
          user_id: userId,
          source: 'manual',
          effective_from: newAssignment.effectiveFrom?.toISOString(),
          effective_to: newAssignment.effectiveTo?.toISOString(),
        },
      }).unwrap();
      handleCreateClose();
      refetch();
    } catch (err) {
      console.error('Failed to create assignment:', err);
      enqueueSnackbar('Failed to create bundle assignment', { variant: 'error' });
    }
  }, [newAssignment, userId, organizationId, createAssignment, handleCreateClose, refetch, enqueueSnackbar]);

  const handleEditOpen = useCallback((assignment: BundleAssignmentResponse) => {
    setSelectedAssignment(assignment);
    setEditStatus(assignment.status);
    setEditEffectiveTo(assignment.effective_to ? new Date(assignment.effective_to) : null);
    setEditDialogOpen(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false);
    setSelectedAssignment(null);
  }, []);

  const handleEditSubmit = useCallback(async () => {
    if (!selectedAssignment) return;

    try {
      await updateStatus({
        assignmentId: selectedAssignment.assignment_id,
        updateAssignmentStatusRequest: {
          status: editStatus,
          effective_to: editEffectiveTo?.toISOString() || undefined,
        },
      }).unwrap();
      handleEditClose();
      refetch();
    } catch (err) {
      console.error('Failed to update assignment:', err);
      enqueueSnackbar('Failed to update bundle assignment', { variant: 'error' });
    }
  }, [selectedAssignment, editStatus, editEffectiveTo, updateStatus, handleEditClose, refetch, enqueueSnackbar]);

  const handleReplaceOpen = useCallback((assignment: BundleAssignmentWithType) => {
    setSelectedAssignment(assignment);
    setReplaceBundle(null);
    setReplaceDialogOpen(true);
  }, []);

  const handleReplaceClose = useCallback(() => {
    setReplaceDialogOpen(false);
    setSelectedAssignment(null);
    setReplaceBundle(null);
  }, []);

  const handleReplaceSubmit = useCallback(async () => {
    if (!selectedAssignment || !replaceBundle) return;

    try {
      await replaceAssignment({
        assignmentId: selectedAssignment.assignment_id,
        replaceBundleRequest: {
          new_bundle_id: replaceBundle.bundle_id,
          organization_id: organizationId,
          user_id: userId,
        },
      }).unwrap();
      enqueueSnackbar('Bundle replaced successfully', { variant: 'success' });
      handleReplaceClose();
      refetch();
    } catch (err) {
      console.error('Failed to replace bundle:', err);
      enqueueSnackbar('Failed to replace bundle', { variant: 'error' });
    }
  }, [selectedAssignment, replaceBundle, replaceAssignment, handleReplaceClose, refetch, enqueueSnackbar]);

  if (isLoading) {
    return <LoadingState message="Loading bundle assignments..." />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Typography variant="body2" className="text-gray-500">
          Bundle assignments directly assigned to this user (not inherited from organization)
        </Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleCreateOpen}>
          Assign Bundle
        </Button>
      </div>

      <Box className="mb-4">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map(status => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {userAssignments.length === 0 ? (
        <div className="text-center py-8">
          <Inventory2 className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <Typography variant="body1" className="text-gray-500">
            No bundle assignments found
          </Typography>
          <Typography variant="body2" className="text-gray-400">
            This user has no direct bundle assignments
          </Typography>
        </div>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bundle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Effective</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userAssignments.map(assignment => (
              <TableRow key={assignment.assignment_id}>
                <TableCell>
                  <span className="font-medium">
                    {assignment.bundle_name || assignment.bundle_id}
                  </span>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(assignment.status) || undefined}
                    label={assignment.status}
                    size="small"
                    color={getStatusColor(assignment.status)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip label={assignment.source} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{formatDateTime(new Date(assignment.effective_from))}</div>
                    {assignment.effective_to && (
                      <div className="text-gray-500">
                        to {formatDateTime(new Date(assignment.effective_to))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell align="right">
                  {assignment.source === 'stripe' && (
                    <Tooltip title="Replace bundle">
                      <IconButton size="small" onClick={() => handleReplaceOpen(assignment as BundleAssignmentWithType)}>
                        <SwapHoriz fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {assignment.source !== 'stripe' && (
                    <Tooltip title="Edit status">
                      <IconButton size="small" onClick={() => handleEditOpen(assignment)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Bundle to User</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <Autocomplete
              options={bundles}
              getOptionLabel={option => option.name}
              value={newAssignment.bundle}
              onChange={(_, value) => setNewAssignment(prev => ({ ...prev, bundle: value }))}
              renderInput={params => (
                <TextField {...params} label="Bundle" required />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div>
                    <div className="font-medium">{option.name}</div>
                    {option.description && (
                      <div className="text-sm text-gray-500">{option.description}</div>
                    )}
                  </div>
                </li>
              )}
            />

            <DatePicker
              label="Effective From (optional)"
              value={newAssignment.effectiveFrom}
              onChange={date => setNewAssignment(prev => ({ ...prev, effectiveFrom: date }))}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />

            <DatePicker
              label="Effective To (optional)"
              value={newAssignment.effectiveTo}
              onChange={date => setNewAssignment(prev => ({ ...prev, effectiveTo: date }))}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={!newAssignment.bundle || isCreating}
          >
            {isCreating ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Status Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="xs" fullWidth>
        <DialogTitle>Update Assignment Status</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={editStatus}
                label="Status"
                onChange={e => setEditStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              label="Effective To"
              value={editEffectiveTo}
              onChange={date => setEditEffectiveTo(date)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace Bundle Dialog */}
      <Dialog open={replaceDialogOpen} onClose={handleReplaceClose} maxWidth="sm" fullWidth>
        <DialogTitle>Replace Bundle</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <Typography variant="body2" className="text-gray-600">
              Replace the current bundle with a different one. This will update the
              Stripe subscription.
            </Typography>

            {selectedAssignment && (
              <div className="bg-gray-50 p-3 rounded">
                <Typography variant="caption" className="text-gray-500">
                  Current bundle
                </Typography>
                <Typography variant="body1" className="font-medium">
                  {selectedAssignment.bundle_name || selectedAssignment.bundle_id}
                </Typography>
              </div>
            )}

            <Autocomplete
              options={bundles.filter(
                b => b.bundle_id !== selectedAssignment?.bundle_id
              )}
              getOptionLabel={option => option.name}
              value={replaceBundle}
              onChange={(_, value) => setReplaceBundle(value)}
              renderInput={params => (
                <TextField {...params} label="New Bundle" required />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div>
                    <div className="font-medium">{option.name}</div>
                    {option.description && (
                      <div className="text-sm text-gray-500">{option.description}</div>
                    )}
                  </div>
                </li>
              )}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReplaceClose}>Cancel</Button>
          <Button
            onClick={handleReplaceSubmit}
            variant="contained"
            disabled={!replaceBundle || isReplacing}
          >
            {isReplacing ? 'Replacing...' : 'Replace'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
